/* Game Mode chest path.
   Replaces the random 5-11 correct-in-a-row chest trigger with a visible milestone: every
   GAME_PATH_SET_SIZE answered questions fills a path on the quiz card and opens the existing
   single-chest reward reel. Chest rewards roll against published odds, and a pity meter
   guarantees an Legendary-or-better reward after GAME_PATH_PITY_LIMIT chests without one.

   app.core.js owns the chest overlay and the reward effects. This module rebinds three of its
   global function declarations (checkMysteryCardTrigger, triggerMysteryCard, applyMysteryReward)
   so the answer handler and the overlay keep calling them by name. */
(function initMemoraGamePath() {
    'use strict';

    const GAME_PATH_SET_SIZE = 10;
    const GAME_PATH_PITY_LIMIT = 3;
    const GAME_PATH_PITY_KEY = 'memoraChestPity';
    const GAME_PATH_BANK_KEY = 'memoraChestBank';
    const GAME_PATH_BANK_LIMIT = 9;
    const GAME_PATH_RARITY_ORDER = ['common', 'rare', 'legendary', 'epic'];
    const GAME_PATH_ODDS = Object.freeze({ common: 50, rare: 30, legendary: 19.5, epic: 0.5 });
    const GAME_PATH_RARITY_LABELS = Object.freeze({ common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' });
    const GAME_PATH_FLAME_TIERS = [
        { min: 8, key: 'inferno', label: 'Inferno' },
        { min: 5, key: 'fire', label: 'On fire' },
        { min: 3, key: 'warm', label: 'Warm' },
        { min: 0, key: 'spark', label: 'Spark' }
    ];

    if (typeof checkMysteryCardTrigger !== 'function' || typeof triggerMysteryCard !== 'function') return;

    // correct: correct answers toward the next chest. banked: chests earned and not yet opened;
    // they wait in the header chip until the student chooses to open one.
    const state = {
        correct: 0,
        banked: readBank(),
        opening: false,
        chestsSinceEpic: readPity(), ledger: null, ready: false
    };

    function readBank() {
        try {
            const value = Number.parseInt(localStorage.getItem(GAME_PATH_BANK_KEY), 10);
            return Number.isFinite(value) && value > 0 ? Math.min(value, GAME_PATH_BANK_LIMIT) : 0;
        } catch (_) { return 0; }
    }



    function readPity() {
        try {
            const value = Number.parseInt(localStorage.getItem(GAME_PATH_PITY_KEY), 10);
            return Number.isFinite(value) && value >= 0 ? Math.min(value, GAME_PATH_PITY_LIMIT - 1) : 0;
        } catch (_) { return 0; }
    }



    // Answer credit, progress, banked chest identities, pity and reserved rolls share xpData.
    // A claim writes its reward and removes its chest in the same files transaction.
    function normalizeChestLedger(raw) {
        if (typeof window !== 'undefined' && window.memoraGameStudy) return window.memoraGameStudy.normalizeLedger(raw, { bank: raw ? 0 : readBank(), pity: readPity() });
        if (raw?.version != null && raw.version !== 1) throw new Error('Study rewards are still loading. Your earned chests have been kept.');
        if (!raw || raw.version !== 1) {
            return { version: 1, revision: 0, correct: 0, chestsSinceEpic: readPity(),
                bank: Array.from({ length: readBank() }, () => ({ id: globalThis.crypto.randomUUID(), rewardId: null })) };
        }
        const seen = new Set();
        return { version: 1, revision: Math.max(0, Math.floor(Number(raw.revision) || 0)),
            correct: Math.max(0, Math.min(GAME_PATH_SET_SIZE - 1, Math.floor(Number(raw.correct) || 0))),
            chestsSinceEpic: Math.max(0, Math.min(GAME_PATH_PITY_LIMIT - 1, Math.floor(Number(raw.chestsSinceEpic) || 0))),
            bank: (Array.isArray(raw.bank) ? raw.bank : []).filter(chest => {
                if (!chest || typeof chest.id !== 'string' || !chest.id || seen.has(chest.id)) return false;
                seen.add(chest.id); return true;
            }).map(chest => ({ id: chest.id, rewardId: typeof chest.rewardId === 'string' ? chest.rewardId : null })) };
    }
    function creditCommittedAnswer(raw) {
        const ledger = normalizeChestLedger(raw);
        ledger.correct++;
        if (ledger.correct >= GAME_PATH_SET_SIZE) {
            ledger.correct = 0;
            ledger.bank.push({ id: globalThis.crypto.randomUUID(), rewardId: null });
        }
        ledger.revision++;
        return ledger;
    }
    function receiveChestLedger(raw) {
        if (!raw || ![1, 2].includes(raw.version) || (state.ledger && raw.revision < state.ledger.revision)) return;
        const before = state.correct;
        state.ledger = normalizeChestLedger(raw);
        state.correct = state.ledger.correct;
        state.banked = state.ledger.bank.length;
        state.chestsSinceEpic = state.ledger.chestsSinceEpic;
        state.ready = true;
        renderGamePath();
        if (state.correct !== before && state.correct > 0) popNode(state.correct - 1);
    }
    function announceChestRecord(record, powerUps = null) {
        applyCommittedXPRecord(record, powerUps);
        try { getXPSyncChannel()?.postMessage({ record, powerUps }); } catch (_) { }
    }
    let chestLedgerLoad;
    function loadChestLedger() {
        if (chestLedgerLoad) return chestLedgerLoad;
        chestLedgerLoad = IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
            const request = tx.objectStore('files').get('xpData');
            request.onerror = () => abort(request.error || new Error('Could not read earned chests.'));
            request.onsuccess = () => {
                const record = request.result || { totalXP: 0, level: 1 };
                const next = { ...record, gamePath: normalizeChestLedger(record.gamePath) };
                if (!record.gamePath || (window.memoraGameStudy && record.gamePath.version !== 2)) write({ storeName: 'files', key: 'xpData', value: next });
                setResult(next);
            };
        }, { timeoutMs: 10000 }).then(record => {
            receiveChestLedger(record.gamePath);
            try { localStorage.removeItem(GAME_PATH_BANK_KEY); localStorage.removeItem(GAME_PATH_PITY_KEY); } catch (_) { }
            return record;
        })
            .catch(error => { chestLedgerLoad = null; throw error; });
        return chestLedgerLoad;
    }
    function resolveChestReward(id) {
        // Retired food reservations retain their earned value as currency.
        const migratedId = ({pantry_berries:'mementos_20',pantry_nectar:'mementos_20',pantry_moon:'mementos_70'})[id] || id;
        const ordinary = MYSTERY_REWARDS.find(reward => reward.id === migratedId);
        if (ordinary) return ordinary;
        const match = /^collection_(pet|background|cosmetic|fragment|study|pose)_(.+)$/.exec(id || '');
        return match ? window.MemoraCompanionModel?.collectionReward(match[1], match[2]) : null;
    }
    function reserveChestReward(chestId, pool) {
        return IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
            const request = tx.objectStore('files').get('xpData');
            request.onerror = () => abort(request.error || new Error('Could not open this chest.'));
            request.onsuccess = () => {
                const record = request.result || {};
                const ledger = normalizeChestLedger(record.gamePath);
                const chest = ledger.bank.find(entry => entry.id === chestId);
                if (!chest) { setResult({ ok: false, reason: 'This chest was already opened in another window.', record }); return; }
                if (!chest.rewardId) {
                    chest.rewardId = pickChestReward(pool, ledger.chestsSinceEpic >= GAME_PATH_PITY_LIMIT - 1).id;
                    ledger.revision++;
                    write({ storeName: 'files', key: 'xpData', value: { ...record, gamePath: ledger } });
                }
                const reward = resolveChestReward(chest.rewardId);
                if (!reward) { abort(new Error('This reward could not load. Please try again.')); return; }
                setResult({ ok: true, reward, record: { ...record, gamePath: ledger } });
            };
        }, { timeoutMs: 10000 }).then(result => { if (result.record?.gamePath) announceChestRecord(result.record); return result; });
    }
    async function claimChestReward(chestId) {
        if (!isGamePathActive()) throw new Error('Turn on Game Mode to collect your reward.');
        const model = window.MemoraCompanionModel;
        if (!model) throw new Error('Your collection is still loading. Please try again.');
        // Finish legacy migration before any compound writer can create a fresh wallet.
        if (typeof window.MemoraCompanions?.refresh === 'function') await window.MemoraCompanions.refresh();
        const result = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
            const files = tx.objectStore('files');
            const values = {};
            let remaining = 3;
            for (const key of ['xpData', 'companionState', 'activePowerUps']) {
                const request = files.get(key);
                request.onerror = () => abort(request.error || new Error('Could not save the reward.'));
                request.onsuccess = () => { values[key] = request.result; if (--remaining === 0) commit(); };
            }
            function commit() {
                try {
                    if (!isGamePathActive()) throw new Error('Turn on Game Mode to collect your reward.');
                    const record = values.xpData || {};
                    const ledger = normalizeChestLedger(record.gamePath);
                    const index = ledger.bank.findIndex(chest => chest.id === chestId);
                    if (index < 0) { setResult({ alreadyClaimed: true, record }); return; }
                    const reward = resolveChestReward(ledger.bank[index].rewardId);
                    if (!reward) throw new Error('Open this chest before collecting its reward.');
                    const effect = reward.effect;
                    if (!values.companionState) throw new Error('Your collection is still loading. Your chest has been kept.');
                    let companion = null, receipt = {}, mementos = 0;
                    const powers = { xpMultiplier: 1, xpMultiplierQuestions: 0, streakShields: 0, growthBoost: 0, growthBoostQuestions: 0, ...values.activePowerUps };
                    if (effect.type === 'mementos' || effect.type === 'collection') {
                        const command = effect.type === 'mementos' ? { type: 'grant', amount: effect.amount, reason: reward.name }
                            : { type: 'collection-grant', kind: effect.kind, id: effect.id };
                        const outcome = model.reduceState(values.companionState, command, true, Date.now());
                        if (!outcome.ok) throw new Error('This reward could not be added to your collection.');
                        companion = outcome.value; receipt = outcome.receipt || {};
                        mementos = effect.type === 'mementos' ? effect.amount : receipt.mementos || 0;
                    } else if (effect.type === 'multiplier') {
                        powers.xpMultiplier = effect.value; powers.xpMultiplierQuestions = effect.questions;
                    } else if (effect.type === 'shield') powers.streakShields = Math.min(MAX_STREAK_SHIELDS, powers.streakShields + effect.charges);
                    else if (effect.type === 'growth_boost') { powers.growthBoost = effect.amount; powers.growthBoostQuestions = effect.questions; }
                    else throw new Error('This reward type is unavailable. Your chest has been kept.');
                    ledger.bank.splice(index, 1);
                    ledger.chestsSinceEpic = GAME_PATH_RARITY_ORDER.indexOf(reward.rarity) >= 2 ? 0 : Math.min(ledger.chestsSinceEpic + 1, GAME_PATH_PITY_LIMIT - 1);
                    ledger.revision++;
                    const next = { ...record, gamePath: ledger };
                    write({ storeName: 'files', key: 'xpData', value: next });
                    if (companion) write({ storeName: 'files', key: 'companionState', value: companion });
                    else {
                        powers._revision = (Number(powers._revision) || 0) + 1;
                        write({ storeName: 'files', key: 'activePowerUps', value: powers });
                    }
                    setResult({ record: next, powers, receipt, reward, mementos });
                } catch (error) { abort(error); }
            }
        }, { timeoutMs: 10000 });
        announceChestRecord(result.record, result.powers);
        if (!result.alreadyClaimed) {
            sessionMementos += result.mementos;
            updatePowerUpIndicator();
            await window.MemoraCompanions.refresh(true);
        }
        return result;
    }

    function chestsUntilGuarantee() {
        return GAME_PATH_PITY_LIMIT - state.chestsSinceEpic;
    }

    function isGamePathActive() {
        return !!isGameMode && window.isMemoraProductFeatureAllowed?.('game-mode') === true;
    }

    function getFlameTier(streak) {
        return GAME_PATH_FLAME_TIERS.find((tier) => streak >= tier.min) || GAME_PATH_FLAME_TIERS[3];
    }

    const FLAME_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M17.556 9.306C16.624 3.814 13.118 2 12 2c-1.118 0-4.623 1.814-5.556 7.306C5.506 14.902 9.096 22 12 22c2.904 0 6.493-7.098 5.556-12.694z"/></svg>';
    const CHEST_SVG = '<svg viewBox="0 0 32 28" aria-hidden="true" focusable="false"><g class="gp-chest-top"><path class="gp-chest-lid" d="M3 12V9.5A7.5 7.5 0 0 1 10.5 2h11A7.5 7.5 0 0 1 29 9.5V12z"/><path class="gp-chest-band" d="M7.5 3.6h2.6V12H7.5zM21.9 3.6h2.6V12h-2.6zM3 10.4h26V12H3z"/></g><path class="gp-chest-body" d="M3 13h26v10a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3z"/><path class="gp-chest-band" d="M7.5 13h2.6v13H7.5zM21.9 13h2.6v13h-2.6zM3 13h26v1.6H3z"/><rect class="gp-chest-lock" x="12.6" y="9" width="6.8" height="8.4" rx="1.6"/><path class="gp-chest-keyhole" d="M16 11.4a1.3 1.3 0 0 1 .7 2.4l.4 2h-2.2l.4-2a1.3 1.3 0 0 1 .7-2.4z"/></svg>';

    function ensurePathElement() {
        let path = document.getElementById('memora-game-path');
        const anchor = document.querySelector('#quiz-card .quiz-card-heading');
        if (!anchor) return null;
        if (path) { if (path.parentElement !== anchor) anchor.prepend(path); return path; }
        path = document.createElement('span');
        path.id = 'memora-game-path';
        path.dataset.gameMode = 'quizzes';
        path.className = 'memora-game-path';
        path.tabIndex = -1;
        path.setAttribute('role', 'group');
        path.setAttribute('aria-label', 'Chest progress');
        path.addEventListener('click', (event) => {
            if (event.target.closest('.gp-open')) openBankedChest();
        });
        path.hidden = true;
        anchor.prepend(path);
        return path;
    }

    function renderGamePath() {
        ensurePathElement();
        const active = isGamePathActive();
        document.body?.classList.toggle('memora-game-path-on', active);
        document.querySelectorAll('.memora-game-path').forEach(path => {
            path.hidden = !active || path.dataset.gameInactive === 'true';
            if (path.hidden) return;
            const mode = path.dataset.gameMode || 'quizzes';
            const done = state.ledger?.points ?? state.correct * 10;
            const progress = mode === 'quizzes' ? done + ' / 100 chest points' : window.memoraGameStudy?.progressLabel(mode, done) || 'Keep studying to earn a chest';
            const guaranteed = chestsUntilGuarantee() <= 1;
            // Weekend 2x rides the chest as a small corner mark instead of its own chip.
            const weekend = window.memoraGameStudy?.studyRewardMultiplier?.() > 1 ? '<span class="gp-weekend" role="img" aria-label="Weekend bonus: double study XP and Mementos">2×</span>' : '';
            const chest = '<span class="gp-chest' + (guaranteed ? ' is-guaranteed' : '') + (state.banked ? ' is-ready' : '') + '" title="Legendary or better within ' + chestsUntilGuarantee() + ' chests' + (weekend ? '\nWeekend: 2× study XP + Mementos' : '') + '">' + CHEST_SVG + weekend + '</span>';
            const open = state.banked ? '<button type="button" class="gp-open"' + (!state.ready ? ' disabled' : '') + ' aria-label="Open earned chest">' + chest + '<span>Open</span>' + (state.banked > 1 ? '<b>' + state.banked + '</b>' : '') + '</button>' : chest;
            const streak = Math.max(0, Number(typeof xpData !== 'undefined' ? xpData.rewardStreak ?? currentStreak : currentStreak) || 0);
            const tier = getFlameTier(streak);
            const flame = mode === 'quizzes' || mode === 'exam' ? '<span class="gp-flame tier-' + tier.key + '" title="' + streak + ' in a row">' + FLAME_SVG + '<span class="gp-flame-val">' + streak + '</span></span>' : '';
            const markup = flame + '<span class="gp-track gp-points-track" role="progressbar" aria-label="Chest progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + done + '" title="' + progress + '"><span style="width:' + done + '%"></span></span>' + open + (mode === 'quizzes' ? '' : '<span class="gp-label" aria-live="polite">' + progress + '</span>');
            if (path._gamePathMarkup !== markup) {
                const focusedOpen = path.contains(document.activeElement) && document.activeElement.closest('.gp-open');
                path._gamePathMarkup = markup; path.innerHTML = markup;
                if (focusedOpen) (path.querySelector('.gp-open') || path).focus({ preventScroll: true });
            }
        });
    }

    function mountGamePath(container, mode) {
        if (!container || !window.memoraGameStudy?.sources[mode]) return null;
        let path = container.querySelector('.memora-game-path');
        if (!path) {
            path = document.createElement('span'); path.className = 'memora-game-path'; path.tabIndex = -1;
            path.setAttribute('role', 'group'); path.setAttribute('aria-label', 'Chest progress');
            path.addEventListener('click', event => { if (event.target.closest('.gp-open')) openBankedChest(); });
            container.appendChild(path);
        }
        if (path.dataset.gameMode !== mode) { path.dataset.gameMode = mode; renderGamePath(); }
        return path;
    }

    function popNode(index) {
        const node = document.querySelector(`#memora-game-path [data-gp-node="${index}"]`);
        if (!node) return;
        node.classList.remove('gp-pop');
        void node.offsetWidth;
        node.classList.add('gp-pop');
    }

    function openBankedChest() {
        if (!isGamePathActive() || !state.ready || state.opening || state.banked < 1 || document.querySelector('.mystery-card-overlay')) return;
        state.opening = true;
        mysteryCardPending = true;
        triggerMysteryCard();
    }

    // The answer handler passes whether the first attempt was correct.
    checkMysteryCardTrigger = function checkMysteryCardTriggerOnPath(isCorrect) {
        if (!isGamePathActive()) return;
        renderGamePath();
    };

    function rollRarity(floor) {
        let roll = Math.random() * 100;
        let rarity = 'common';
        for (const key of GAME_PATH_RARITY_ORDER) {
            roll -= GAME_PATH_ODDS[key];
            if (roll < 0) { rarity = key; break; }
        }
        if (floor && GAME_PATH_RARITY_ORDER.indexOf(rarity) < GAME_PATH_RARITY_ORDER.indexOf(floor)) {
            rarity = floor; // Pity upgrades lower tiers without increasing the 0.5% Epic chance.
        }
        return rarity;
    }

    function sampleChestPool(base, collectibles) {
        const sampled = [...base];
        for (const rarity of GAME_PATH_RARITY_ORDER) {
            const matching = collectibles.filter(reward => reward.rarity === rarity);
            const fragments = matching.filter(reward => reward.effect?.kind === 'fragment');
            const candidates = [...new Map((rarity === 'epic' && fragments.length ? fragments : matching).map(reward => [reward.id, reward])).values()];
            const count = ['legendary', 'epic'].includes(rarity) ? 1 : 1 + Math.floor(Math.random() * 2);
            for (let i = 0; i < Math.min(count, candidates.length); i++) {
                const pick = i + Math.floor(Math.random() * (candidates.length - i));
                [candidates[i], candidates[pick]] = [candidates[pick], candidates[i]];
                sampled.push(candidates[i]);
            }
        }
        return sampled;
    }

    function chestReelMotion(winningIndex) {
        return { duration: 5000, frames: [
            { index: 2, offset: 0, easing: 'cubic-bezier(.45,0,.85,.4)' },
            // One continuous ease-out from cruising speed to rest, so the reel never stalls and restarts.
            { index: 6, offset: .12, easing: 'cubic-bezier(.22,.24,.12,1)' },
            { index: winningIndex, offset: 1 }
        ] };
    }

    // The reel streaks up out of the opened chest as a glowing sliver, unfurls sideways, then
    // snaps open. Returns when it has fully opened; the spin can start just before that.
    const REEL_EMERGE_MS = 1050;
    function emergeReel(reel, from, color) {
        const to = reel.getBoundingClientRect();
        const dy = (from.top + from.height / 2) - (to.top + to.height / 2);
        // The halo is a gradient layer whose opacity fades: an animated drop-shadow moves pixels,
        // which Chrome will only run on the main thread.
        const halo = reel.querySelector('.gp-reel-glow');
        halo.style.setProperty('--gp-glow', color);
        halo.animate([
            { opacity: 1 }, { opacity: .9, offset: .38 }, { opacity: .6, offset: .66 }, { opacity: .45, offset: .84 }, { opacity: 0 }
        ], { duration: REEL_EMERGE_MS });
        const glow = (bright, sat) => `brightness(${bright}) saturate(${sat})`;
        reel.querySelector('.gp-reel-window').animate([
            { opacity: 0, offset: 0 }, { opacity: 0, offset: .74 }, { opacity: 1, offset: .82 }, { opacity: 0 }
        ], { duration: REEL_EMERGE_MS + 500, pseudoElement: '::after' });
        reel.querySelector('.gp-reel-window').animate([
            { transform: 'translateX(-40%)', offset: 0 }, { transform: 'translateX(-40%)', offset: .72 }, { transform: 'translateX(106.7%)' }
        ], { duration: REEL_EMERGE_MS + 500, easing: 'ease-in-out', pseudoElement: '::after' });
        return reel.animate([
            { transform: `translateY(${dy}px) scale(.03, .05)`, opacity: 0, filter: glow(4, .4, 30), easing: 'cubic-bezier(.3,.8,.4,1)' },
            { transform: `translateY(${dy * .7}px) scale(.05, .07)`, opacity: 1, filter: glow(4, .4, 30), offset: .1, easing: 'cubic-bezier(.2,.9,.3,1)' },
            { transform: 'translateY(0) scale(.08, .035)', opacity: 1, filter: glow(3.6, .5, 26), offset: .38, easing: 'cubic-bezier(.7,0,.2,1)' },
            { transform: 'translateY(0) scale(1.05, .035)', opacity: 1, filter: glow(3, .6, 18), offset: .66, easing: 'cubic-bezier(.3,1.5,.5,1)' },
            { transform: 'translateY(0) scale(1, 1.09)', opacity: 1, filter: glow(1.5, 1, 14), offset: .84, easing: 'ease-out' },
            { transform: 'none', opacity: 1, filter: glow(1, 1, 0) }
        ], { duration: REEL_EMERGE_MS });
    }

    function cubicBezier(x1, y1, x2, y2) {
        const at = (a, b, t) => ((1 - 3 * b + 3 * a) * t + (3 * b - 6 * a)) * t * t + 3 * a * t;
        return x => {
            let lo = 0, hi = 1, t = x;
            for (let i = 0; i < 24; i++) {
                const v = at(x1, x2, t);
                if (Math.abs(v - x) < 1e-5) break;
                if (v < x) lo = t; else hi = t;
                t = (lo + hi) / 2;
            }
            return at(y1, y2, t);
        };
    }

    // The reel path is fixed before the spin starts, so the per-card tilt and the marker highlight
    // are precomputed as compositor animations. Nothing runs per frame on the main thread, which
    // keeps the spin smooth when the CPU is busy or throttled.
    function animateReelDepth(track, motion) {
        const cards = [...track.children];
        const eases = motion.frames.map(frame => {
            const match = /cubic-bezier\(([^)]+)\)/.exec(frame.easing || '');
            return match ? cubicBezier(...match[1].split(',').map(Number)) : t => t;
        });
        const indexAt = p => {
            const frames = motion.frames;
            for (let k = 0; k < frames.length - 1; k++) {
                if (p <= frames[k + 1].offset || k === frames.length - 2) {
                    const span = frames[k + 1].offset - frames[k].offset;
                    return frames[k].index + (frames[k + 1].index - frames[k].index) * eases[k](span ? Math.min(1, Math.max(0, (p - frames[k].offset) / span)) : 1);
                }
            }
            return frames.at(-1).index;
        };
        // The reel only moves forward, so the moment it reaches a position can be found by bisection.
        const pAt = position => {
            let lo = 0, hi = 1;
            for (let i = 0; i < 30; i++) { const mid = (lo + hi) / 2; if (indexAt(mid) < position) lo = mid; else hi = mid; }
            return (lo + hi) / 2;
        };
        const tilt = distance => 'perspective(700px) rotateY(' + Math.max(-28, Math.min(28, distance * -10)) + 'deg) scale(' + (1 - Math.min(.1, Math.abs(distance) * .035)) + ')';
        // Chrome will not composite an animation whose element is off-screen and ticks it on the main
        // thread instead, so each card only animates while it is inside the window. Outside it the
        // tilt is already at its clamp, which an inline style holds.
        const VISIBLE = 3;
        // Keyframes sit at fixed distances from the card, so a coarse grid still lands exactly on the
        // highlight ramp and the edge tilt. Every keyframe costs style time when the spin starts.
        const start = indexAt(0), end = indexAt(1);
        const windowed = (card, index, range, step, frame, options = {}) => {
            const span = [];
            if (index - range < start && start < index + range) span.push({ p: 0, distance: index - start });
            for (let k = Math.round(range / step); k >= -Math.round(range / step); k--) {
                const position = index - k * step;
                if (position > start && position < end) span.push({ p: pAt(position), distance: k * step });
            }
            if (index - range < end && end < index + range) span.push({ p: 1, distance: index - end });
            if (span.length < 2) return [];
            const from = span[0].p, to = span.at(-1).p;
            return [card.animate(span.map(s => ({ offset: (s.p - from) / (to - from), ...frame(s.distance) })), {
                ...options, delay: from * motion.duration, duration: (to - from) * motion.duration, fill: to === 1 ? 'forwards' : 'none'
            })];
        };
        // Write every inline style before the first animate(): each animate() flushes style, so
        // interleaving the two recalculates the whole reel once per card.
        cards.forEach((card, index) => { card.style.transform = tilt(Math.sign(index - start) * VISIBLE); });
        const animations = cards.flatMap((card, index) => [
            ...windowed(card, index, VISIBLE, .5, distance => ({ transform: tilt(distance) })),
            ...windowed(card, index, .8, .2, distance => ({ opacity: Math.min(1, Math.max(0, (.6 - Math.abs(distance)) / .2)) }), { pseudoElement: '::after' })
        ]);
        // Hand the resting tilt to inline styles before canceling, so the landing transition still
        // eases the neighbours flat instead of snapping them.
        return () => {
            cards.forEach((card, index) => { card.style.transform = Math.abs(index - end) < VISIBLE ? tilt(index - end) : ''; });
            animations.forEach(animation => animation.cancel());
            getComputedStyle(cards[0]).transform;
            cards.forEach(card => { card.style.transform = ''; });
        };
    }

    // Watches frame pacing while the chest opens; a slow device sheds particles instead of frames.
    function watchFramePacing(onSlow) {
        const deltas = [];
        let frame, last = performance.now(), slow = false;
        const tick = now => {
            deltas.push(now - last); last = now;
            if (deltas.length > 12) deltas.shift();
            if (!slow && deltas.length >= 8 && [...deltas].sort((a, b) => a - b)[deltas.length >> 1] > 25) { slow = true; onSlow(); }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return { get slow() { return slow; }, stop() { cancelAnimationFrame(frame); } };
    }

    function pickChestReward(pool, guaranteed) {
        const rarity = rollRarity(guaranteed ? 'legendary' : null);
        const matching = pool.filter((reward) => reward.rarity === rarity);
        const fragments = matching.filter(reward => reward.effect?.kind === 'fragment');
        const source = rarity === 'epic' && fragments.length ? fragments : matching;
        if (!source.length) throw new Error('This reward tier is still loading. Your chest has been kept.');
        return source[Math.floor(Math.random() * source.length)];
    }

    // Reel filler rolls the same published odds as the prize, so the reel never hints at the result.
    function reelFillerReward(pool, guaranteed) {
        try { return pickChestReward(pool, guaranteed); }
        catch (_) { return pool[Math.floor(Math.random() * pool.length)]; }
    }

    /* Odds and pity: one quiet line under the box heading. */
    function buildOddsPanel(guaranteed) {
        const odds = document.createElement('div');
        odds.className = 'gp-odds';
        const oddsFor = (key) => guaranteed
            ? (key === 'epic' ? GAME_PATH_ODDS.epic : key === 'legendary' ? 100-GAME_PATH_ODDS.epic : 0)
            : GAME_PATH_ODDS[key];
        const keys = GAME_PATH_RARITY_ORDER.filter((key) => oddsFor(key) > 0);
        const bar = keys.map((key) =>
            `<span class="gp-odds-seg" style="flex:${oddsFor(key)};--r:var(--gp-${key})"></span>`).join('');
        const legend = keys.map((key) =>
            `<span class="gp-odds-key" style="--r:var(--gp-${key})"><i></i>${GAME_PATH_RARITY_LABELS[key]} ${oddsFor(key)}%</span>`).join('');
        const filled = guaranteed ? GAME_PATH_PITY_LIMIT : state.chestsSinceEpic;
        const pips = Array.from({ length: GAME_PATH_PITY_LIMIT }, (_, i) =>
            `<span class="gp-pity-pip${i < filled ? ' is-filled' : ''}"></span>`).join('');
        const left = chestsUntilGuarantee();
        const pityText = guaranteed
            ? 'This chest is Legendary or better'
            : `Legendary or better within ${left} chest${left === 1 ? '' : 's'}`;
        odds.innerHTML = `
            <div class="gp-odds-bar">${bar}</div>
            <div class="gp-odds-legend">${legend}<span class="gp-pity"><span class="gp-pity-pips">${pips}</span>${pityText}</span></div>`;
        return odds;
    }

    /* One sealed 3D chest opens beneath a reel of rewards. The center marker selects the prize. */
    const GAME_BOX_GLYPHS = Object.freeze({
        xp: '<path d="M54 22 34 54h14l-4 24 22-34H52z" />',
        shield: '<path d="M50 24 30 31v16c0 13 8.6 23 20 28 11.4-5 20-15 20-28V31z" /><path d="M50 32v35c-7.5-3.8-13-10.6-13-19.6V36z" style="fill:rgba(255,255,255,.35)" />',
        comeback: '<path d="M33 52a17 17 0 1 0 5-12" style="fill:none;stroke:currentColor;stroke-width:6.5;stroke-linecap:round"/><path d="M28 30v14h14z" />',
        clean: '<circle cx="50" cy="50" r="19" style="fill:none;stroke:currentColor;stroke-width:5"/><circle cx="50" cy="50" r="9.5" style="fill:none;stroke:currentColor;stroke-width:5"/><circle cx="50" cy="50" r="3.2"/>',
        cosmetic: '<path d="M50 23l7 16.5 18 1.6-13.6 11.8 4.1 17.6L50 61.1 34.5 70.5l4.1-17.6L25 41.1l18-1.6z" />',
        streak: '<path d="M50 21c3 10 15 16 15 31a15 15 0 0 1-30 0c0-7.5 4-12 6.5-16 1 5.5 3.5 8.5 6.5 9.5-1.2-9.5.2-18 2-24.5z" />',
        memento: '<path d="M37 28h26l11 15-24 31-24-31z" /><path d="M26 43h48M43 28l7 46 7-46" style="fill:none;stroke:rgba(0,0,0,.28);stroke-width:2;stroke-linejoin:round"/>',
        treat: '<circle cx="39" cy="58" r="11"/><circle cx="61" cy="58" r="11"/><circle cx="50" cy="41" r="11"/><path d="M50 30c1-6 6-9 12-9" style="fill:none;stroke:currentColor;stroke-width:4;stroke-linecap:round"/>'
    });
    const GAME_BOX_BURST = Object.freeze({ common: 45, rare: 75, legendary: 150, epic: 180 });
    let gameBoxUid = 0;

    function escapeText(text) {
        return String(text ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
    }

    function describeReward(reward) {
        const effect = reward?.effect || { type: 'xp' };
        const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
        switch (effect.type) {
            case 'collection':
                return { glyph: 'cosmetic', kind: reward.name, show: effect.kind === 'fragment' ? 'Puzzle piece' : 'Unlocked', detail: reward.description + ' · Choose it in Marketplace' };
            case 'mementos':
                return { glyph: 'memento', kind: 'Mementos', amount: effect.amount, unit: 'Mementos', detail: 'Spend them in the companion shop' };
            case 'xp':
            case 'xp_confetti':
                return { glyph: 'xp', kind: 'XP', amount: effect.amount, unit: 'XP', detail: 'Added to your level now' };
            case 'multiplier':
                return { glyph: 'mult', mult: `${effect.value}×`, kind: `${effect.value}× XP`, show: count(effect.questions, 'answer', 'answers'),
                    detail: `Your next ${effect.questions} study steps earn ${effect.value}× base XP` };
            case 'shield':
                return { glyph: 'shield', kind: 'Streak Shield', show: count(effect.charges, 'shield', 'shields'),
                    detail: `A miss won’t break your streak. Hold up to ${MAX_STREAK_SHIELDS}` };
            case 'growth_boost':
                return { glyph: 'comeback', kind: 'Growth Bonus', show: `+${effect.amount} XP each`, detail: `On the next ${effect.questions} questions you’d missed before` };
            default:
                return { glyph: 'cosmetic', kind: reward?.name || 'Reward', show: reward?.description || '', detail: '' };
        }
    }

    // Colour comes from the inherited --r custom property, so the art follows rarity and theme.
    function boxEmblem(view) {
        const id = `gpe${gameBoxUid++}`;
        const glyph = view.glyph === 'mult'
            ? `<text x="50" y="${view.mult.length > 3 ? 58 : 61}" text-anchor="middle" font-family="Georgia, serif" font-size="${view.mult.length > 3 ? 22 : 30}" font-style="italic">${escapeText(view.mult)}</text>`
            : GAME_BOX_GLYPHS[view.glyph] || GAME_BOX_GLYPHS.xp;
        return `<svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
            <defs>
                <radialGradient id="${id}a" cx="50%" cy="32%" r="70%">
                    <stop offset="0" style="stop-color:color-mix(in srgb, var(--r) 50%, #fff)"/>
                    <stop offset=".55" style="stop-color:var(--r)"/>
                    <stop offset="1" style="stop-color:color-mix(in srgb, var(--r) 40%, #000)"/>
                </radialGradient>
                <linearGradient id="${id}b" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" style="stop-color:#fff;stop-opacity:.55"/><stop offset="1" style="stop-color:#fff;stop-opacity:0"/>
                </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="46" style="fill:none;stroke:var(--r);stroke-opacity:.35;stroke-width:1.2;stroke-dasharray:2 4"/>
            <circle cx="50" cy="50" r="40" fill="url(#${id}a)"/>
            <circle cx="50" cy="50" r="40" style="fill:none;stroke:color-mix(in srgb, var(--r) 40%, #000);stroke-width:2"/>
            <circle cx="50" cy="50" r="34" style="fill:none;stroke:#fff;stroke-opacity:.22;stroke-width:1"/>
            <g style="fill:#f8efd9;color:#f8efd9;filter:drop-shadow(0 2px 1.5px rgba(0,0,0,.35))">${glyph}</g>
            <ellipse cx="50" cy="28" rx="26" ry="12" fill="url(#${id}b)" opacity=".7"/>
        </svg>`;
    }

    function boxMarkup() {
        const view = { glyph: 'cosmetic' };
        return `
            <button type="button" class="gp-box" style="--r:var(--gp-accent)" aria-label="Open mystery chest">
                <span class="gp-box-mover">
                    <span class="gp-box-pool"></span>
                    <span class="gp-box-shadow"></span>
                    <span class="gp-box-beam"></span>
                    <span class="gp-box-persp"><span class="gp-box-scene"><span class="gp-box-sway"><span class="gp-box-rig">
                        <span class="gp-box-bodyg">
                            <span class="f i-floor"></span>
                            <span class="f i-wall i-back"></span><span class="f i-wall i-front"></span><span class="f i-wall i-left"></span><span class="f i-wall i-right"></span>
                            <span class="f i-glow"></span>
                            <span class="f b-bottom"></span>
                            <span class="f b-back texture"></span><span class="f b-left texture"></span><span class="f b-right texture"></span>
                            <span class="f b-front texture"><span class="inlay"></span><span class="plate">${boxEmblem(view)}</span></span>
                        </span>
                        <span class="gp-box-lid"><span class="gp-box-lidc">
                            <span class="f l-under"></span>
                            <span class="f l-side l-back texture"></span><span class="f l-side l-left texture"></span><span class="f l-side l-right texture"></span>
                            <span class="f l-side l-front texture"><span class="clasp"></span></span>
                            <span class="f l-top texture"><span class="frame"></span></span>
                        </span></span>
                    </span></span></span></span>
                    <span class="gp-box-leak"></span>
                    <span class="gp-box-prize">${boxEmblem(view)}</span>
                </span>
            </button>`;
    }

    function reelCardMarkup(reward, index) {
        const view = describeReward(reward);
        return `<div class="gp-reel-card" data-reward-id="${escapeText(reward.id)}" data-index="${index}" style="--r:var(--gp-${reward.rarity})">
            <span class="gp-reel-rarity">${GAME_PATH_RARITY_LABELS[reward.rarity]}</span>
            <span class="gp-reel-emblem">${reward.artSrc ? `<img src="${escapeText(reward.artSrc)}" style="transform:scaleX(${reward.artFlipX ? -1 : 1})" alt="" loading="eager">` : boxEmblem(view)}</span>
            <span class="gp-reel-name">${escapeText(view.kind)}</span>
            <span class="gp-reel-value">${escapeText(view.amount ? `+${view.amount} ${view.unit}` : view.show)}</span>
        </div>`;
    }

    function prefersLiteMotion() {
        if (document.body.classList.contains('smoke-mode')) return true;
        try { if (typeof shouldUseLiteMysteryFX === 'function' && shouldUseLiteMysteryFX()) return true; } catch (_) { }
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    }

    // Custom properties come back unresolved; let the browser compute them for canvas use.
    function resolveColor(el, prop) {
        const probe = document.createElement('i');
        probe.style.cssText = `position:absolute;visibility:hidden;color:var(${prop})`;
        el.appendChild(probe);
        const color = getComputedStyle(probe).color;
        probe.remove();
        return color;
    }

    function boxRattle(strength, n = 34) {
        const body = [];
        const lid = [];
        for (let k = 0; k <= n; k++) {
            const t = k / n;
            const e = t * t;
            const dir = k % 2 ? 1 : -1;
            const amp = (0.8 + e * 7) * strength;
            body.push({ transform: `translate3d(${dir * amp * (0.5 + Math.random() * 0.5)}px, ${-Math.abs(Math.sin(k * 1.7)) * amp * 0.5}px, 0) rotateZ(${dir * (0.4 + e * 3) * strength}deg)` });
            lid.push({ transform: `translateY(${-(Math.random() * (2 + e * 9)) * strength}px) rotateX(${(Math.random() * (2 + e * 10)) * strength}deg)` });
        }
        return { body, lid };
    }

    /* Additive particle canvas covering the viewport: seam sparks, the burst, drifting embers and
       a ring on the floor. */
    function createBoxFx(canvas, lite) {
        const ctx = canvas.getContext('2d');
        let parts = [];
        let rings = [];
        let running = false;
        let alive = true;
        // Particle counts scale with this; a slow device drops to a fraction instead of dropping frames.
        let budget = 1;
        let last = 0;
        const size = () => {
            const d = Math.min(1.5, window.devicePixelRatio || 1);
            canvas.width = innerWidth * d;
            canvas.height = innerHeight * d;
            ctx.setTransform(d, 0, 0, d, 0, 0);
        };
        size();
        window.addEventListener('resize', size);

        // Motion is measured in 60 fps frames of elapsed time, so a throttled frame moves particles
        // further rather than making the whole burst play in slow motion.
        function tick(now) {
            if (!alive) return;
            const dt = Math.min(4, (now - (last || now - 16.667)) / 16.667);
            last = now;
            const damp = Math.pow(0.965, dt);
            ctx.clearRect(0, 0, innerWidth, innerHeight);
            ctx.globalCompositeOperation = 'lighter';
            rings = rings.filter((r) => {
                r.age += dt;
                const t = r.age / 40;
                if (t >= 1) return false;
                const rad = r.r + (1 - Math.pow(1 - t, 3)) * 230;
                ctx.strokeStyle = r.color;
                ctx.globalAlpha = (1 - t) * 0.8;
                ctx.lineWidth = 2.5 * (1 - t) + 0.5;
                ctx.beginPath();
                ctx.ellipse(r.x, r.y, rad, rad * 0.26, 0, 0, Math.PI * 2);
                ctx.stroke();
                return true;
            });
            parts = parts.filter((p) => {
                p.age += dt;
                if (p.age < 0) return true;
                if (p.age > p.life) return false;
                p.vx *= damp;
                p.vy = p.vy * damp + p.g * dt;
                p.x += p.vx * dt;
                p.y += p.vy * dt;
                const t = p.age / p.life;
                ctx.globalAlpha = p.ember ? Math.sin(t * Math.PI) * 0.75 : (1 - t);
                ctx.fillStyle = p.color;
                ctx.strokeStyle = p.color;
                if (p.star) {
                    const s = p.size * 2.4 * (1 - t * 0.5);
                    ctx.beginPath(); ctx.moveTo(p.x, p.y - s); ctx.lineTo(p.x + s * 0.22, p.y); ctx.lineTo(p.x, p.y + s); ctx.lineTo(p.x - s * 0.22, p.y); ctx.closePath(); ctx.fill();
                    ctx.beginPath(); ctx.moveTo(p.x - s, p.y); ctx.lineTo(p.x, p.y + s * 0.22); ctx.lineTo(p.x + s, p.y); ctx.lineTo(p.x, p.y - s * 0.22); ctx.closePath(); ctx.fill();
                } else if (!p.ember && Math.hypot(p.vx, p.vy) > 2) {
                    ctx.lineWidth = p.size;
                    ctx.lineCap = 'round';
                    ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x - p.vx * 2.4, p.y - p.vy * 2.4); ctx.stroke();
                } else {
                    ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
                }
                return true;
            });
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
            if (parts.length || rings.length) requestAnimationFrame(tick);
            else { running = false; last = 0; ctx.clearRect(0, 0, innerWidth, innerHeight); }
        }
        function run() { if (!running && alive) { running = true; requestAnimationFrame(tick); } }

        return {
            set budget(value) { budget = value; },
            spray(x, y, color, count, speed, life, star, w = 40, up = false, g = 0.12) {
                if (lite) return;
                for (let k = 0, n = Math.ceil(count * budget); k < n; k++) {
                    const a = up ? (-Math.PI / 2 + (Math.random() - 0.5) * 1.3) : Math.random() * Math.PI * 2;
                    const sp = speed * (0.35 + Math.random() * 0.9);
                    parts.push({ x: x + (Math.random() - 0.5) * w, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, g, life: life * (0.6 + Math.random() * 0.7), age: 0,
                        size: 1.2 + Math.random() * 2.4, color: Math.random() < 0.25 ? '#fff4dc' : color, star: star || Math.random() < 0.1 });
                }
                run();
            },
            dust(x, y, w, color) {
                if (lite) return;
                for (let k = 0, n = Math.ceil(30 * budget); k < n; k++) {
                    parts.push({ x: x + (Math.random() - 0.5) * w * 0.55, y: y - Math.random() * 40, vx: (Math.random() - 0.5) * 0.3, vy: -0.3 - Math.random() * 0.8,
                        g: -0.003, life: 140 + Math.random() * 140, age: -Math.random() * 80, size: 0.8 + Math.random() * 1.6, color, ember: true });
                }
                run();
            },
            ring(x, y, w, color) {
                if (lite) return;
                rings.push({ x, y, r: w * 0.3, age: 0, color });
                run();
            },
            destroy() {
                alive = false;
                parts = [];
                rings = [];
                window.removeEventListener('resize', size);
            }
        };
    }

    function openChestOverlay(pool, guaranteed, chestId) {
        if (document.querySelector('.mystery-card-overlay')) return;
        const previousFocus = document.activeElement;
        const openerPath = previousFocus?.closest?.('.memora-game-path');
        const lite = prefersLiteMotion();
        const overlay = document.createElement('dialog');
        overlay.className = 'mystery-card-overlay gp-box-overlay gp-reel-overlay';
        overlay.dataset.gameMode = state.ledger.bank.find(chest => chest.id === chestId)?.mode || 'quizzes';
        overlay.tabIndex = -1;
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'gp-box-title');
        overlay.innerHTML = `
            <button type="button" class="gp-box-close" aria-label="Close reward chest">×</button>
            <div class="gp-box-stage">
                <div class="gp-box-head">
                    <div class="gp-box-eyebrow">Chest earned in ${window.memoraGameStudy?.sources[state.ledger.bank.find(chest => chest.id === chestId)?.mode || 'quizzes']?.label || 'Quizzes'}</div>
                    <h2 class="gp-box-title" id="gp-box-title">Your next reward</h2>
                    <p class="gp-box-sub">One chest. Open it and see where it lands.</p>
                </div>
                <div class="gp-reel" aria-hidden="true">
                    <span class="gp-reel-glow"></span>
                    <div class="gp-reel-window"><div class="gp-reel-track"></div></div>
                    <span class="gp-reel-marker"></span>
                </div>
                <div class="gp-box-row">${boxMarkup()}</div>
                <div class="gp-reel-result" role="status" aria-live="polite"><span class="gp-reel-hint">Every 100 study points earns a chest.</span></div>
                <button type="button" class="gp-spin-btn">Open chest <span aria-hidden="true">→</span></button>
                <button type="button" class="gp-skip-btn" hidden>Skip animation</button>
                <div class="gp-chest-actions">
                    <button type="button" class="gp-box-continue" hidden>Return to studying <span aria-hidden="true">→</span></button>
                    <button type="button" class="gp-box-another gp-spin-btn" hidden>Open Another <span aria-hidden="true">→</span></button>
                </div>
            </div>
            <canvas class="gp-box-fx" aria-hidden="true"></canvas>`;
        overlay.querySelector('.gp-box-head').insertAdjacentElement('afterend', buildOddsPanel(guaranteed));
        document.body.appendChild(overlay);
        overlay.showModal();
        document.body.classList.add('gp-chest-open');
        requestAnimationFrame(() => overlay.classList.add('active'));

        const fx = createBoxFx(overlay.querySelector('.gp-box-fx'), lite);
        const slot = overlay.querySelector('.gp-box');
        const track = overlay.querySelector('.gp-reel-track');
        const titleEl = overlay.querySelector('.gp-box-title');
        const subEl = overlay.querySelector('.gp-box-sub');
        const resultEl = overlay.querySelector('.gp-reel-result');
        const openBtn = overlay.querySelector('.gp-spin-btn');
        const continueBtn = overlay.querySelector('.gp-box-continue');
        const anotherBtn = overlay.querySelector('.gp-box-another');
        const skipBtn = overlay.querySelector('.gp-skip-btn');
        const closeBtn = overlay.querySelector('.gp-box-close');
        const reelPosition = (index) => `translateX(calc(-${index} * (var(--gp-card-width) + var(--gp-card-gap)) - var(--gp-card-width) / 2))`;
        // Built once: opening only swaps the off-screen winning card, so the visible reel never changes.
        const winningIndex = 34;
        const sequence = Array.from({ length: winningIndex + 6 }, () => reelFillerReward(pool, guaranteed));
        track.innerHTML = sequence.map(reelCardMarkup).join('');
        track.style.transform = reelPosition(2);
        let picked = false;
        let spinning = false;
        let closed = false;
        let spinAnimation, rattleAnimation, emergeAnimation;
        let skipRequested = false;
        let stopReelDepth = () => {};
        let pacing = { slow: false, stop() {} };
        skipBtn.addEventListener('click', () => {
            skipRequested = true; skipBtn.disabled = true;
            rattleAnimation?.finish(); emergeAnimation?.finish(); spinAnimation?.finish();
        });

        let retryClaim = null;
        const finishReward = async (reward, view) => {
            spinning = true;
            openBtn.disabled = true;
            closeBtn.disabled = true;
            overlay.setAttribute('aria-busy', 'true');
            let claim;
            try {
                claim = await applyMysteryReward(reward, chestId);
            } catch (error) {
                spinning = false;
                retryClaim = () => finishReward(reward, view);
                openBtn.hidden = false;
                openBtn.disabled = false;
                openBtn.textContent = 'Retry reward';
                closeBtn.disabled = false;
                overlay.setAttribute('aria-busy', 'false');
                resultEl.textContent = `${error.message} Your chest has been kept.`;
                openBtn.focus({ preventScroll: true });
                return;
            }
            retryClaim = null;
            overlay.dataset.rarity = reward.rarity;
            overlay.classList.toggle('gp-lite', lite);
            titleEl.textContent = view.kind;
            subEl.textContent = claim.alreadyClaimed ? 'Already collected in another window.' : claim.receipt?.puzzleComplete ? 'Puzzle complete. Your companion is ready in Marketplace.' : 'Your reward is ready.';
            const duplicate = claim.receipt?.duplicate;
            resultEl.innerHTML = claim.alreadyClaimed ? '<span>This chest has already been added to your collection.</span>'
                : `<span class="gp-result-rarity">${GAME_PATH_RARITY_LABELS[reward.rarity]}</span>
                    <strong>${escapeText(duplicate ? `+${claim.receipt.mementos} Mementos` : view.amount ? `+${view.amount} ${view.unit}` : view.show)}</strong>
                    <span>${escapeText(duplicate ? `${claim.receipt.name} was already in your collection.` : claim.receipt?.name && reward.effect?.kind === 'fragment' ? `${claim.receipt.name} added to your puzzle.` : view.detail)}</span>`;
            if (!claim.alreadyClaimed && !duplicate && reward.artSrc) {
                const art = document.createElement('img'); art.className = 'gp-reward-art'; art.src = reward.artSrc; art.alt = reward.name;
                if (reward.artFlipX) art.style.transform = 'scaleX(-1)';
                resultEl.prepend(art);
            }
            if (!claim.alreadyClaimed && !duplicate && reward.effect?.type === 'collection') {
                const badge = document.createElement('span'); badge.className = 'gp-new-item'; badge.textContent = 'New'; resultEl.prepend(badge);
                const equip = document.createElement('button'); equip.type = 'button'; equip.className = 'gp-equip-now'; equip.textContent = reward.effect.kind === 'fragment' ? 'View puzzle' : 'Equip now';
                equip.addEventListener('click', async () => {
                    equip.disabled = true;
                    try {
                        const kind = reward.effect.kind;
                        if (kind !== 'fragment') {
                            const result = await window.MemoraCompanions.equipReward(reward);
                            if (result.ok) { window.memoraGameStudy.syncSurfaces(); equip.textContent = 'Equipped'; return; }
                        }
                        close();
                        await window.MemoraCompanions.openStore(kind === 'study' ? 'study' : kind === 'cosmetic' ? 'wardrobe' : kind === 'background' ? 'backgrounds' : kind === 'fragment' ? 'aurelia' : 'pets', kind === 'fragment' ? claim.receipt?.petId || window.MemoraCompanionModel.collectionItem('fragment', reward.effect.id)?.petId : undefined);
                    } catch (_) { equip.disabled = false; }
                });
                resultEl.append(equip);
            }
            if (duplicate) overlay.classList.add('gp-duplicate');
            if (!claim.alreadyClaimed && !lite) {
                const winner = overlay.querySelector('.is-winner');
                const rect = winner.getBoundingClientRect();
                const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2, color = resolveColor(winner, '--r');
                fx.spray(cx, cy, color, GAME_BOX_BURST[reward.rarity], 6, 55, true, rect.width);
                if (overlay.classList.contains('is-jackpot')) {
                    fx.dust(cx, rect.bottom, rect.width * 3, color);
                    setTimeout(() => { if (!closed) { fx.spray(cx, rect.bottom, color, GAME_BOX_BURST[reward.rarity], 10, 85, true, rect.width * 2, true); } }, 280);
                }
            }
            overlay.setAttribute('aria-busy', 'false');
            spinning = false;
            closeBtn.disabled = false;
            openBtn.hidden = true;
            continueBtn.hidden = false;
            anotherBtn.hidden = state.banked < 1;
            continueBtn.classList.add('show');
            continueBtn.focus({ preventScroll: true });
        };

        const open = async () => {
            if (retryClaim) { await retryClaim(); return; }
            if (picked || closed) return;
            picked = true;
            spinning = true;
            skipRequested = false; skipBtn.disabled = false; skipBtn.hidden = lite;
            slot.disabled = true;
            openBtn.disabled = true;
            openBtn.textContent = 'Opening…';
            closeBtn.disabled = true;
            overlay.focus({ preventScroll: true });
            overlay.classList.add('is-spinning');
            overlay.setAttribute('aria-busy', 'true');
            titleEl.textContent = 'Opening your chest…';
            subEl.textContent = lite ? 'Revealing your reward.' : '';
            resultEl.textContent = 'Opening…';
            // Roll once. The reel is presentation only; its landing card is the actual grant.
            let reservation;
            try {
                reservation = await reserveChestReward(chestId, pool);
                if (!reservation.ok) throw new Error(reservation.reason);
            } catch (error) {
                picked = false; spinning = false;
                slot.disabled = false; openBtn.disabled = false; closeBtn.disabled = false;
                openBtn.textContent = 'Try opening again';
                skipBtn.hidden = true;
                overlay.classList.remove('is-spinning'); overlay.setAttribute('aria-busy', 'false');
                resultEl.textContent = error.message; return;
            }
            const reward = reservation.reward;
            const view = describeReward(reward);
            overlay.dataset.rarity = reward.rarity;
            overlay.classList.toggle('gp-lite', lite);
            track.children[winningIndex].outerHTML = reelCardMarkup(reward, winningIndex);

            try {
                if (!lite && !skipRequested) {
                    const lid = slot.querySelector('.gp-box-lid');
                    const color = resolveColor(slot, '--r');
                    const later = (ms, fn) => setTimeout(() => { if (!closed && !skipRequested) fn(); }, ms);
                    pacing = watchFramePacing(() => { fx.budget = .4; });
                    // Build-up: the rattle grows while light leaks from the lid seam.
                    const rattle = boxRattle(0.9, 26);
                    rattleAnimation = slot.querySelector('.gp-box-sway').animate(rattle.body, { duration: 900 });
                    lid.animate(rattle.lid, { duration: 900 });
                    slot.querySelector('.gp-box-leak').animate([{ opacity: 0 }, { opacity: .35, offset: .6 }, { opacity: 1 }], { duration: 900, easing: 'ease-in' });
                    later(450, () => { const seam = lid.getBoundingClientRect(); fx.spray(seam.left + seam.width / 2, seam.bottom, color, 14, 3, 30, false, seam.width * .8, true); });
                    await rattleAnimation.finished;
                    if (!skipRequested) {
                    // Burst: the lid blows off, the chest squashes, and a beam of light shoots up.
                    const mouth = lid.getBoundingClientRect();
                    const mx = mouth.left + mouth.width / 2, my = mouth.top + mouth.height / 2;
                    lid.animate([
                        { transform: 'none', opacity: 1 },
                        { transform: 'translateY(-120px) rotateX(-60deg) rotateZ(-8deg)', opacity: 1, offset: 0.45 },
                        { transform: 'translateY(-210px) rotateX(-110deg) rotateZ(-14deg)', opacity: 0 }
                    ], { duration: 760, easing: 'cubic-bezier(.15,.9,.3,1)', fill: 'forwards' });
                    slot.querySelector('.gp-box-mover').animate([{ scale: '1' }, { scale: '1.08 .86', offset: .22 }, { scale: '.96 1.06', offset: .55 }, { scale: '1' }], { duration: 520, easing: 'ease-out' });
                    slot.querySelector('.gp-box-leak').animate([{ opacity: 1 }, { opacity: 0 }], { duration: 700, easing: 'ease-out' });
                    slot.querySelector('.i-glow').animate([{ opacity: 0 }, { opacity: 1, offset: .1 }, { opacity: .55 }], { duration: 1400, fill: 'forwards' });
                    slot.querySelector('.gp-box-pool').animate([{ opacity: 0 }, { opacity: 1, offset: .15 }, { opacity: .35 }], { duration: 1600, fill: 'forwards' });
                    slot.querySelector('.gp-box-beam').animate([
                        { opacity: 0, transform: 'scaleY(0) scaleX(.5)' },
                        { opacity: 1, transform: 'scaleY(1.15) scaleX(1.1)', offset: .22 },
                        { opacity: .8, transform: 'scaleY(1) scaleX(.9)', offset: .6 },
                        { opacity: 0, transform: 'scaleY(1) scaleX(.3)' }
                    ], { duration: 1500, easing: 'ease-out' });
                    fx.ring(mx, my, mouth.width, color);
                    fx.spray(mx, my, color, 90, 9, 60, true, mouth.width * .7, true);
                    fx.dust(mx, my, mouth.width * 2, color);
                    // The reel rides the beam out of the chest, unfurls, and snaps open.
                    const reel = overlay.querySelector('.gp-reel');
                    overlay.classList.add('is-reel-out');
                    emergeAnimation = emergeReel(reel, mouth, color);
                    later(REEL_EMERGE_MS * .7, () => {
                        const r = reel.getBoundingClientRect(), cy = r.top + r.height / 2;
                        fx.spray(r.left + r.width * .06, cy, color, 26, 6, 40, true, 10);
                        fx.spray(r.right - r.width * .06, cy, color, 26, 6, 40, true, 10);
                    });
                    later(REEL_EMERGE_MS * .84, () => {
                        reel.animate([{ translate: '0 0' }, { translate: '0 5px' }, { translate: '0 -2px' }, { translate: '0 0' }], { duration: 260, easing: 'ease-out' });
                    });
                    await new Promise(resolve => setTimeout(resolve, REEL_EMERGE_MS * .8));
                    }
                    if (!skipRequested && !closed) {
                    const motion = chestReelMotion(winningIndex);
                    spinAnimation = track.animate(motion.frames.map(({ index, ...frame }) => ({ ...frame, transform: reelPosition(index) })), { duration: motion.duration, fill: 'forwards' });
                    stopReelDepth = animateReelDepth(track, motion);
                    await spinAnimation.finished;
                    }
                } else {
                    slot.querySelector('.gp-box-lid').style.opacity = '0';
                }
            } catch (_) {
                // A canceled browser animation still settles the already rolled reward.
            }
            pacing.stop();
            stopReelDepth();
            skipBtn.hidden = true;
            if (closed) return;
            overlay.classList.add('is-reel-out');
            track.style.transform = reelPosition(winningIndex);
            spinAnimation?.cancel();
            const winner = track.children[winningIndex];
            winner.classList.add('is-winner');
            overlay.classList.remove('is-spinning');
            overlay.classList.add('is-landed');
            overlay.classList.toggle('is-jackpot', !lite && GAME_PATH_RARITY_ORDER.indexOf(reward.rarity) >= 2);
            overlay.style.setProperty('--gp-winner', `var(--gp-${reward.rarity})`);
            slot.style.setProperty('--r', `var(--gp-${reward.rarity})`);
            slot.querySelector('.i-glow').style.opacity = '1';
            await finishReward(reward, view);
        };

        const close = () => {
            if (closed || spinning) return false;
            closed = true;
            pacing.stop();
            stopReelDepth();
            fx.destroy();
            overlay.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
            document.removeEventListener('keydown', onKey, true);
            document.body.classList.remove('gp-chest-open');
            window.MemoraModalCoordinator?.markClosed('game-mode-chest', { restore: false });
            overlay.close();
            closeMysteryCardOverlay(overlay);
            // Dismissal before opening preserves the banked chest.
            state.opening = false;
            mysteryCardPending = false;
            renderGamePath();
            const returnFocus = previousFocus?.isConnected ? previousFocus : openerPath?.isConnected && !openerPath.hidden ? openerPath.querySelector('.gp-open') || openerPath : null;
            returnFocus?.focus({ preventScroll: true });
            return true;
        };
        const onKey = (event) => {
            // The reward owns keyboard input while native study dialogs remain underneath.
            event.stopImmediatePropagation();
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                close();
            } else if (event.key === 'Tab') {
                const buttons = [...overlay.querySelectorAll('button')].filter((button) => !button.disabled && !button.hidden);
                const index = buttons.indexOf(document.activeElement);
                event.preventDefault();
                const next = index < 0 ? (event.shiftKey ? buttons.length - 1 : 0)
                    : (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
                (buttons[next] || overlay).focus({ preventScroll: true });
            }
        };
        document.addEventListener('keydown', onKey, true);
        overlay.addEventListener('cancel', event => { event.preventDefault(); close(); });
        window.MemoraModalCoordinator?.open({ id: 'game-mode-chest', element: overlay, opener: previousFocus,
            parentLayerId: window.MemoraModalCoordinator.getStack().at(-1), restoreFocus: false, trapFocus: true,
            isOpen: () => overlay.open && !closed, close, initialFocus: openBtn });
        slot.addEventListener('click', open);
        openBtn.addEventListener('click', open);
        closeBtn.addEventListener('click', close);
        continueBtn.addEventListener('click', close);
        anotherBtn.addEventListener('click', () => {
            if (!close()) return;
            // Remove the old, fading dialog before the next chest checks for an open overlay.
            overlay.remove();
            openBankedChest();
        });
        openBtn.focus({ preventScroll: true });
        return overlay;
    }

    let pendingGuarantee = false;
    triggerMysteryCard = function triggerMysteryCardOnPath() {
        if (!isGamePathActive() || !state.ready || !state.banked || document.querySelector('.mystery-card-overlay')) return;
        state.opening = true; mysteryCardPending = true;
        const mode = state.ledger.bank[0].mode || 'quizzes';
        const profileKey = getCurrentMysteryStreakProfile();
        const profileRewards = [...MYSTERY_REWARDS, ...(window.MemoraCompanions?.chestRewards() || [])].filter((reward) => {
            if (!window.memoraGameStudy.rewardAllowed(reward, mode)) return false;
            if (!Array.isArray(reward.profiles) || reward.profiles.length === 0) return true;
            return reward.profiles.includes('all') || reward.profiles.includes(profileKey);
        });
        const shieldsFull = activePowerUps.streakShields >= MAX_STREAK_SHIELDS;
        const usable = (list) => list.filter((reward) => !(shieldsFull && reward.effect?.type === 'shield'));
        const eligible = usable(profileRewards);
        const pool = sampleChestPool(eligible.filter(reward => reward.effect?.type !== 'collection'), eligible.filter(reward => reward.effect?.type === 'collection'));
        pendingGuarantee = chestsUntilGuarantee() <= 1;
        openChestOverlay(pool, pendingGuarantee, state.ledger.bank[0].id);
    };

    applyMysteryReward = function applyMysteryRewardOnPath(reward, chestId) {
        if (!chestId) return Promise.reject(new Error('An earned chest is required.'));
        return claimChestReward(chestId);
    };

    function scheduleRender() { requestAnimationFrame(renderGamePath); }
    document.addEventListener('click', (event) => {
        if (event.target?.closest?.('#game-mode-toggle, .mystery-continue-btn')) setTimeout(renderGamePath, 0);
    });
    window.addEventListener('memora:game-mode-changed', scheduleRender);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleRender, { once: true });
    else scheduleRender();

    /* Level-up card: a compact corner card instead of the centered popup, full-screen flash,
       and falling confetti. celebrateLevelUp() in app.core.js calls these three by name. */
    const GAME_LEVEL_TIER_METALS = Object.freeze({
        bronze: ['#e3a26a', '#9a5b2a'], silver: ['#f1f1f1', '#9ca3af'], gold: ['#ffe27a', '#c08a12'],
        platinum: ['#fafaf9', '#a8a29e'], diamond: ['#c5f6ff', '#38bdf8'], master: ['#d8b4fe', '#7c3aed'],
        grandmaster: ['#fca5a5', '#b91c1c'], legend: ['#fcd34d', '#d97706'], mythic: ['#f9a8d4', '#be185d'],
        immortal: ['#fef3c7', '#f59e0b'], transcendent: ['#e0e7ff', '#818cf8'], eternal: ['#ffffff', '#fbbf24']
    });
    const GAME_LEVEL_CARD_MS = 10000;
    let levelCardTimer = 0;

    function capitalize(text) { return String(text || '').charAt(0).toUpperCase() + String(text || '').slice(1); }

    function nextTierInfo(level) {
        const tier = getLevelTier(level);
        for (let probe = level + 1; probe <= level + 1000; probe += 1) {
            if (getLevelTier(probe) !== tier) return { tier: getLevelTier(probe), levels: probe - level };
        }
        return null;
    }

    triggerLevelUpFlash = function triggerLevelUpFlashNone() {};
    triggerConfetti = function triggerConfettiNone() {};

    showLevelUpPopup = function showLevelUpCornerCard(previousLevel = xpData.level - 1) {
        const newLevel = xpData.level;
        const tier = getLevelTier(newLevel);
        const tierChanged = getLevelTier(previousLevel) !== tier;
        const [metalHi, metalLo] = GAME_LEVEL_TIER_METALS[tier] || GAME_LEVEL_TIER_METALS.bronze;
        const floor = getXPForLevel(newLevel);
        const span = Math.max(1, getXPForLevel(newLevel + 1) - floor);
        const into = Math.max(0, Math.min(span, xpData.totalXP - floor));
        const upcoming = nextTierInfo(newLevel);
        const tierMementos = tierChanged && typeof LEVEL_TIER_MEMENTOS !== 'undefined' ? LEVEL_TIER_MEMENTOS[tier] : 0;
        const sub = tierChanged
            ? `${capitalize(tier)} tier unlocked${tierMementos ? ` · +${tierMementos} Mementos` : ''}`
            : (upcoming ? `${capitalize(tier)} tier · ${upcoming.levels} to ${capitalize(upcoming.tier)}` : `${capitalize(tier)} tier`);
        const sparks = Array.from({ length: 10 }, (_, i) => `<i style="--a:${i * 36}deg"></i>`).join('');

        document.querySelector('.gp-level-card')?.remove();
        clearTimeout(levelCardTimer);
        const card = document.createElement('div');
        card.className = `gp-level-card${tierChanged ? ' is-new-tier' : ''}`;
        card.setAttribute('role', 'status');
        card.setAttribute('aria-live', 'polite');
        card.style.setProperty('--tier-hi', metalHi);
        card.style.setProperty('--tier-lo', metalLo);
        card.innerHTML = `
            <div class="gp-level-medal" aria-hidden="true">
                <span class="gp-level-rays"></span>
                <span class="gp-level-sparks">${sparks}</span>
                <svg viewBox="0 0 48 52"><path d="M24 2 45 14v24L24 50 3 38V14z"/></svg>
                <b>${newLevel}</b>
            </div>
            <div class="gp-level-text">
                <div class="gp-level-eyebrow">Level up</div>
                <div class="gp-level-title">Level ${newLevel}</div>
                <div class="gp-level-sub">${sub}</div>
                <div class="gp-level-bar"><span style="--fill:${(into / span) * 100}%"></span></div>
                <div class="gp-level-xp">${into} / ${span} XP to level ${newLevel + 1}</div>
                <div class="gp-level-bonus">${getLevelXPBonus(newLevel).toFixed(2)}× study XP</div>
                <div class="gp-level-actions"><button type="button" data-level-badges>See badges</button><button type="button" data-level-close aria-label="Dismiss level-up notification">Dismiss</button></div>
            </div>`;
        const dismiss = () => {
            clearTimeout(levelCardTimer);
            card.classList.remove('is-visible');
            setTimeout(() => card.remove(), 350);
        };
        const schedule = () => { clearTimeout(levelCardTimer); levelCardTimer = setTimeout(dismiss, GAME_LEVEL_CARD_MS); };
        card.querySelector('[data-level-badges]').onclick = () => { dismiss(); openStatsModal(); switchStatsTab('badges'); document.getElementById('stats-tab-badges')?.focus(); };
        card.querySelector('[data-level-close]').onclick = dismiss;
        card.addEventListener('pointerenter', () => clearTimeout(levelCardTimer));
        card.addEventListener('pointerleave', () => { if (!card.contains(document.activeElement)) schedule(); });
        card.addEventListener('focusin', () => clearTimeout(levelCardTimer));
        card.addEventListener('focusout', event => { if (!card.contains(event.relatedTarget)) schedule(); });
        getLevelUpEffectsRoot().appendChild(card);
        requestAnimationFrame(() => requestAnimationFrame(() => card.classList.add('is-visible')));
        schedule();
    };

    window.memoraGamePath = Object.freeze({ render: renderGamePath, mount: mountGamePath, openBankedChest, chestsUntilGuarantee, creditAnswer: creditCommittedAnswer, receiveRecord: receiveChestLedger, load: loadChestLedger });
    void loadChestLedger().catch(error => console.warn('Could not load earned chests:', error));
})();
